import { Component, ViewEncapsulation, HostBinding, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Observable, Subscription, map, catchError } from 'rxjs';
import { MatRippleModule } from '@angular/material/core';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { UNDERSCORE, OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { ScheduleBusinessOrder } from '../../models/schedule-business-order';
import { ScheduleBusinessOrderService } from '../../services/schedule-business-order.service';
import { ModelInfoPipe } from '../../../shared/pipes/model-info.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';

const sortWay = OrderByDirection.DESC;
const sortField = 'customerOrderNumber';

@Component({
	selector: 'copo-sb-order-tracking-list',
	templateUrl: './search-sb-order-list.component.html',
	animations: pageTransitions,
	styleUrl: './search-sb-order-list.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		VirtualScrollerDirective,
		CdkScrollable,
		TranslateModule,
		NgFor,
		MatRippleModule,
		RouterLink,
		NgIf,
		LoadingSpinnerComponent,
		ModelInfoPipe
	]
})
export class SearchSBOrderListComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	user: User;
	filter: string | undefined;

	readonly UNDERSCORE = UNDERSCORE;

	requiredFields: (keyof ScheduleBusinessOrder)[] = [
		'supplierOrderNumber',
		'customerOrderNumber',
		'customerArticleReference',
		'businessStatus',
		'steelgradeDescription'
	];

	private readonly searchTextSubscription?: Subscription;

	constructor(
		private readonly orderService: ScheduleBusinessOrderService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly activedRoute: ActivatedRoute
	) {
		this.user = this.userService.user;

		if (!this.router.isActive(
			'search',
			{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
		)) {
			return;
		}
		this.searchTextSubscription = this.activedRoute.params.subscribe(p => {
			const searchText = <string | undefined> p['query'];
			this.filter = searchText ? this.orderService.createLuceneFilter(searchText) : undefined;
		});
	}

	get dataId() {
		const dataId = 'order-tracking';
		return this.filter === undefined ? dataId : `${ dataId }-${ this.filter }`;
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<ScheduleBusinessOrder[]>) => void) {
		provideData(
			this.orderService.getScheduleBusinessOrders(
				this.requiredFields,
				this.filter,
				index,
				limit,
				new OrderBy(sortField, sortWay),
				false
			).pipe(
				map(res => res.results),
				catchError((err: unknown) => {
					console.error('error retrieving batches', err);
					return [];
				})
			)
		);
	}

	getProductName(order: ScheduleBusinessOrder) {
		return order.steelgradeDescription || UNDERSCORE;
	}

	getProductType() {
		return 'order-tracking.steelgrade';
	}

	getBusinessStatus(order: ScheduleBusinessOrder) {
		return order.businessStatusDescription || UNDERSCORE;
	}
}
